import { gql } from "@apollo/client";
import AllAdminsAndManagersPermissionRows from "@src/features/libraryItem/course/shareButton/AllAdminsAndManagersPermissionRows";
import { CourseSharingPermissionsCourseFragment } from "@src/features/libraryItem/course/shareButton/ShareCourseDialog.generated";
import { useCreateUserSharingPermissionsMutation } from "@src/features/libraryItem/course/shareButton/ShareDialog.generated";
import { ModuleSharingPermissionsModuleFragment } from "@src/features/libraryItem/course/shareButton/ShareModuleDialog.generated";
import SharingPermissionRow from "@src/features/libraryItem/course/shareButton/SharingPermissionRow";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Separator } from "@src/lib/ui/Separator";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { useGetAllEmployeesQuery } from "@src/features/users/ui/operations.generated";
import { useToast } from "@src/lib/hooks/useToast";
import useUser from "@src/features/app/hooks/useUser";
import {
  GraphqlOperations,
  SharingPermission,
  UserType,
} from "@src/types.generated";
import { pluralize } from "@src/lib/utils/strings";
import { sortBy } from "lodash";
import { FC, useCallback, useMemo, useState } from "react";
import { ShareSkillDialog_SkillFragment } from "@src/features/libraryItem/skill/ShareSkillDialog.generated";
import { TrainingResourceSharingPermissionsTrainingResourceFragment } from "@src/features/libraryItem/course/shareButton/ShareTrainingResourceDialog.generated";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import { PERMISSION_TO_DISPLAY_NAME } from "@src/features/libraryItem/course/shareButton/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import { Checkbox } from "@src/lib/ui/Checkbox";
import ChevronDown from "@src/lib/icons/18px/chevron-down";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  course?: CourseSharingPermissionsCourseFragment;
  module?: ModuleSharingPermissionsModuleFragment;
  skill?: ShareSkillDialog_SkillFragment;
  trainingResource?: TrainingResourceSharingPermissionsTrainingResourceFragment;
  hasEditAdminAndManagerSharingPermission: boolean;
  hasShareWithIndividualsPermission: boolean;
};

const ShareDialog: FC<Props> = ({
  open,
  onOpenChange,
  course,
  module,
  skill,
  trainingResource,
  hasEditAdminAndManagerSharingPermission,
  hasShareWithIndividualsPermission,
}) => {
  const content = course ?? module ?? skill ?? trainingResource;
  const isCourse = !!course;
  const isTrainingResource = !!trainingResource;
  const { user } = useUser();
  const { addToast, addErrorToast } = useToast();
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(
    SharingPermission.Edit,
  );

  const { data: employeesData } = useGetAllEmployeesQuery({
    variables: {
      userTypes: [UserType.Manager, UserType.Admin],
    },
  });

  const previouslySelectedEmployeeIds = useMemo(() => {
    const set = new Set<number>(
      content?.libraryItem.sharingPermissions.individuals.map(
        (usp) => usp.user.id,
      ),
    );
    if (content?.libraryItem.createdBy?.id) {
      set.add(content?.libraryItem.createdBy.id);
    }
    return set;
  }, [
    content?.libraryItem.sharingPermissions.individuals,
    content?.libraryItem.createdBy?.id,
  ]);

  const availableEmployees = useMemo(() => {
    const allEmployees = employeesData?.Employees.objects || [];
    return allEmployees.filter((e) => !previouslySelectedEmployeeIds.has(e.id));
  }, [employeesData, previouslySelectedEmployeeIds]);

  const selectedNames = useMemo(() => {
    return availableEmployees
      .filter((e) => selectedUserIds.includes(e.id))
      .map((e) => e.name)
      .join(", ");
  }, [availableEmployees, selectedUserIds]);

  const [createUserSharingPermissions, { loading }] =
    useCreateUserSharingPermissionsMutation({
      variables: {
        input: {
          courseId: course?.id,
          moduleId: module?.id,
          skillId: skill?.id,
          trainingResourceId: trainingResource?.id,
          permission: selectedPermission,
          userIds: selectedUserIds,
        },
      },
      refetchQueries: [
        GraphqlOperations.Query.CourseSharingPermissions,
        GraphqlOperations.Query.ModuleSharingPermissions,
        GraphqlOperations.Query.ShareSkillDialog_Skill,
        GraphqlOperations.Query.TrainingResourceSharingPermissions,
      ],
      onCompleted: (data) => {
        if (data.createUserSharingPermissions.success) {
          addToast({
            message: `${selectedUserIds.length} ${pluralize(
              "employee",
              selectedUserIds.length,
            )} invited`,
          });
          setSelectedUserIds([]);
        } else {
          addErrorToast({ ...data, callsite: "createSharingPermissions" });
        }
      },
    });

  const individualsWithPermissions = useMemo(
    () =>
      sortBy(content?.libraryItem.sharingPermissions.individuals, "user.name"),
    [content],
  );

  const permissionOptions = useMemo(() => {
    const options = [SharingPermission.Edit];
    if (!isTrainingResource) options.push(SharingPermission.Comment);
    if (!isCourse) options.push(SharingPermission.ViewInLibrary);
    return options;
  }, [isCourse, isTrainingResource]);

  const handleInvite = useCallback(() => {
    createUserSharingPermissions();
  }, [createUserSharingPermissions]);

  const placeholder =
    user?.userType === UserType.Admin
      ? "Invite admins or managers"
      : "Invite managers at your location";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Collaborators settings</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          {hasShareWithIndividualsPermission ? (
            <div className="flex flex-col gap-3">
              <Text type="P3" fontWeight="SemiBold">
                Individual collaborators
              </Text>
              <div className="flex min-w-0 items-center gap-2">
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={popoverOpen}
                      className="flex w-0 min-w-0 flex-1 justify-between"
                    >
                      <span className="truncate">
                        {selectedNames || placeholder}
                      </span>
                      <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    disablePortal
                  >
                    <Command>
                      <CommandInput
                        placeholder="Search people..."
                        className="h-9"
                      />
                      <CommandList>
                        <CommandEmpty>No results found.</CommandEmpty>
                        <CommandGroup>
                          {availableEmployees.map((employee) => {
                            const isChecked = selectedUserIds.includes(
                              employee.id,
                            );
                            return (
                              <CommandItem
                                key={employee.id}
                                value={employee.name || String(employee.id)}
                                onSelect={() => {
                                  if (isChecked) {
                                    setSelectedUserIds(
                                      selectedUserIds.filter(
                                        (id) => id !== employee.id,
                                      ),
                                    );
                                  } else {
                                    setSelectedUserIds([
                                      ...selectedUserIds,
                                      employee.id,
                                    ]);
                                  }
                                  setPopoverOpen(true);
                                }}
                                className="gap-2"
                              >
                                <Checkbox checked={isChecked} />
                                <Text type="P2">{employee.name}</Text>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Select
                  value={selectedPermission}
                  onValueChange={(val) =>
                    setSelectedPermission(val as SharingPermission)
                  }
                >
                  <SelectTrigger className="w-[120px] shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {permissionOptions.map((p) => (
                      <SelectItem key={p} value={p}>
                        {PERMISSION_TO_DISPLAY_NAME[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleInvite}
                  loading={loading}
                  disabled={selectedUserIds.length === 0}
                  className="shrink-0"
                >
                  Invite
                </Button>
              </div>
              {individualsWithPermissions.length > 0 && (
                <div className="flex flex-col gap-1">
                  {individualsWithPermissions.map((userSharingPermission) => (
                    <SharingPermissionRow
                      userSharingPermission={userSharingPermission}
                      courseId={course?.id}
                      moduleId={module?.id}
                      skillId={skill?.id}
                      trainingResourceId={trainingResource?.id}
                      key={userSharingPermission.id}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : null}
          {content &&
            (course || module || trainingResource) &&
            hasShareWithIndividualsPermission && <Separator />}
          {content && (course || module || trainingResource) && (
            <AllAdminsAndManagersPermissionRows
              courseId={course?.id}
              moduleId={module?.id}
              trainingResourceId={trainingResource?.id}
              canEdit={hasEditAdminAndManagerSharingPermission}
              coursePublishState={course?.libraryItem.publishState}
              sharingPermissions={content.libraryItem.sharingPermissions}
              commentOptionEnabled={!isTrainingResource}
              viewPermissionEnabled={!isCourse}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;

gql`
  mutation createUserSharingPermissions(
    $input: CreateUserSharingPermissionsInput!
  ) {
    createUserSharingPermissions(input: $input) {
      success
      error {
        code
        message
      }
      userSharingPermissions {
        ...UserSharingPermission
      }
    }
  }
`;
